// ==============================================================================
// API Service for Patent Search System
// ==============================================================================

import {
  Phase1InputData,
  GenerateQueryRequest,
  GenerateQueryResponse,
  ExecuteQueryRequest,
  ExecuteQueryResponse,
  ExtractPatentsResponse,
  AnalyzePayload,
  JobResponse,
  SearchFormula,
  RealizationMethod,
  ComponentData,
  QualityData,
  SessionData,
  BackendAnalysisHistory,
} from '../types';
import { API_BASE_URL, fetchWithAuth } from '../config/api';
import { MOCK_MODE } from '../config/mock';

// ==============================================================================
// Query Generation API (New Feature)
// ==============================================================================

export const queryAPI = {
  /**
   * Generate search queries from user goal using AI
   */
  async generate(request: GenerateQueryRequest): Promise<GenerateQueryResponse> {
    const response = await fetchWithAuth(`/api/query/generate`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '検索式の生成に失敗しました');
    }

    return response.json();
  },

  /**
   * Execute search with generated queries
   */
  async execute(request: ExecuteQueryRequest): Promise<ExecuteQueryResponse> {
    const response = await fetchWithAuth(`/api/query/execute`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '検索の実行に失敗しました');
    }

    return response.json();
  },

  /**
   * Extract patents from search results
   */
  async extractPatents(goal: string): Promise<ExtractPatentsResponse> {
    const response = await fetchWithAuth(`/api/query/extract`, {
      method: 'POST',
      body: JSON.stringify({ goal }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '特許情報の取得に失敗しました');
    }

    return response.json();
  },
};

// ==============================================================================
// Jobs API (New Feature)
// ==============================================================================

export const jobsAPI = {
  /**
   * Create a new analysis job
   */
  async create(payload: AnalyzePayload): Promise<JobResponse> {
    const response = await fetchWithAuth(`/api/jobs`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'ジョブの作成に失敗しました');
    }

    return response.json();
  },

  /**
   * Get job status and details
   */
  async get(jobId: string): Promise<JobResponse> {
    const response = await fetchWithAuth(`/api/jobs/${jobId}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'ジョブの取得に失敗しました');
    }

    return response.json();
  },

  /**
   * Get list of all jobs
   */
  async list(limit: number = 50, offset: number = 0): Promise<JobResponse[]> {
    const response = await fetchWithAuth(`/api/jobs?limit=${limit}&offset=${offset}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'ジョブ一覧の取得に失敗しました');
    }

    return response.json();
  },

  /**
   * Download job result file
   */
  getDownloadUrl(jobId: string, filename: string): string {
    return `${API_BASE_URL}/api/jobs/${jobId}/download/${filename}`;
  },
};

// ==============================================================================
// Auth API (New)
// ==============================================================================

export const authAPI = {
  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<{ username: string; email: string; role: string }> {
    if (MOCK_MODE) {
      const username = (localStorage.getItem('auth_username') || 'mock-user').toString();
      return { username, email: 'mock@example.com', role: 'user' };
    }
    const response = await fetchWithAuth('/api/auth/me');

    if (!response.ok) {
      throw new Error('ユーザー情報の取得に失敗しました');
    }

    return response.json();
  },

  /**
   * Get user history
   */
  async getUserHistory(
    userId: string,
    opts?: { signal?: AbortSignal }
  ): Promise<{ history: BackendAnalysisHistory[] }> {
    if (MOCK_MODE) {
      return { history: [] };
    }
    // Avoid cached/stale responses on CDN/proxies (history is highly dynamic).
    const response = await fetchWithAuth(`/api/auth/history/${userId}`, {
      signal: opts?.signal,
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });

    if (!response.ok) {
      throw new Error('履歴の取得に失敗しました');
    }

    return response.json();
  },
};

// ==============================================================================
// Phase 1 API (Original)
// ==============================================================================

const extractApiErrorMessage = (detail: unknown, fallback: string): string => {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg?: unknown }).msg || '');
        }
        return '';
      })
      .filter(Boolean);
    if (messages.length > 0) return messages.join('\n');
  }
  if (detail && typeof detail === 'object') {
    const obj = detail as { message?: unknown; error?: unknown; detail?: unknown };
    return extractApiErrorMessage(obj.message || obj.error || obj.detail, fallback);
  }
  return fallback;
};

export const phase1API = {
  /**
   * Start workflow with user input
   */
  async startWorkflow(data: Phase1InputData): Promise<{ session_id: string; title?: string }> {
    const response = await fetchWithAuth(`/api/phase1/start-workflow`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(extractApiErrorMessage(error.detail, 'ワークフローの開始に失敗しました'));
    }

    return response.json();
  },
};

// ==============================================================================
// Phase 2 API (Original)
// ==============================================================================

export const phase2API = {
  /**
   * Generate search formulas
   */
  async generateFormulas(sessionId: string): Promise<{ formulas: SearchFormula[] }> {
    const response = await fetchWithAuth(`/api/phase2/generate-formulas?session_id=${sessionId}`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('検索式の生成に失敗しました');
    }

    return response.json();
  },

  /**
   * Select formulas
   */
  async selectFormulas(sessionId: string, formulaIds: string[]): Promise<void> {
    const response = await fetchWithAuth(`/api/phase2/select-formulas?session_id=${sessionId}`, {
      method: 'POST',
      body: JSON.stringify(formulaIds),
    });

    if (!response.ok) {
      throw new Error('検索式の選択に失敗しました');
    }
  },

  /**
   * Execute final search
   * limits: [{ id: 'FORMULA-1', max_patents: 1000 }, ...]
   */
  async executeFinalSearch(
    sessionId: string,
    limits: { id: string; max_patents: number }[]
  ): Promise<SessionData> {
    const response = await fetchWithAuth(
      `/api/phase2/execute-final-search?session_id=${sessionId}`,
      {
        method: 'POST',
        body: JSON.stringify({ formula_limits: limits }),
      }
    );

    if (!response.ok) {
      throw new Error('特許検索の実行に失敗しました');
    }

    return response.json();
  },

  /**
   * Extract realization methods
   */
  async extractRealizationMethods(
    sessionId: string
  ): Promise<{ realization_methods: RealizationMethod[] }> {
    const response = await fetchWithAuth(
      `/api/phase2/extract-realization-methods?session_id=${sessionId}`,
      { method: 'POST' }
    );

    if (!response.ok) {
      throw new Error('実現方法の抽出に失敗しました');
    }

    return response.json();
  },

  /**
   * Select realization methods
   */
  async selectRealizationMethods(sessionId: string, methodIds: string[]): Promise<void> {
    const response = await fetchWithAuth(
      `/api/phase2/select-realization-methods?session_id=${sessionId}`,
      {
        method: 'POST',
        body: JSON.stringify(methodIds),
      }
    );

    if (!response.ok) {
      throw new Error('実現方法の選択に失敗しました');
    }
  },

  /**
   * Extract components
   */
  async extractComponents(sessionId: string): Promise<ComponentData> {
    const response = await fetchWithAuth(`/api/phase2/extract-components?session_id=${sessionId}`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('構成要素の抽出に失敗しました');
    }

    return response.json();
  },

  /**
   * Judge patent quality
   */
  async judgePatentQuality(sessionId: string): Promise<QualityData> {
    const response = await fetchWithAuth(
      `/api/phase2/judge-patent-quality?session_id=${sessionId}`,
      { method: 'POST' }
    );

    if (!response.ok) {
      throw new Error('特許品質評価に失敗しました');
    }

    return response.json();
  },
};

// ==============================================================================
// Phase 3 API (Original)
// ==============================================================================

export const phase3API = {
  /**
   * Generate Excel report
   */
  async generateExcel(sessionId: string): Promise<{ status: string; job_id: string }> {
    const response = await fetchWithAuth(`/api/phase3/generate-excel?session_id=${sessionId}`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('Excel生成に失敗しました');
    }

    return response.json();
  },

  /**
   * List workflows for a user (includes in-progress sessions)
   */
  async getUserWorkflows(
    userId: string,
    opts?: { signal?: AbortSignal }
  ): Promise<{
    user_id: string;
    workflow_count: number;
    workflows: Array<{
      session_id: string;
      title?: string;
      current_phase: number;
      current_step: number;
      status: string;
      async_task_id?: string | null;
      progress_message?: string | null;
      progress_current?: number;
      progress_total?: number | null;
      job?: {
        job_id: string;
        job_type: string;
        status: string;
        progress_message?: string | null;
        progress_current?: number;
        progress_total?: number | null;
        error_message?: string | null;
        retry_attempt?: number | null;
        retry_max?: number | null;
        is_retrying?: boolean;
        queue?: {
          queue_position?: number | null;
          queued_total?: number;
          running_total?: number;
          concurrency?: number;
        };
      } | null;
      created_at?: string;
    }>;
  }> {
    if (MOCK_MODE) {
      return {
        user_id: userId,
        workflow_count: 0,
        workflows: [],
      };
    }
    const response = await fetchWithAuth(`/api/phase3/user/${userId}/workflows`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
      signal: opts?.signal,
    });
    if (!response.ok) {
      throw new Error('ワークフロー一覧の取得に失敗しました');
    }
    return response.json();
  },

  /**
   * Delete a workflow session (idempotent)
   */
  async deleteSession(sessionId: string): Promise<{ session_id: string; deleted: boolean }> {
    if (MOCK_MODE) {
      return { session_id: sessionId, deleted: true };
    }
    const response = await fetchWithAuth(`/api/phase3/session/${sessionId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'セッションの削除に失敗しました');
    }
    return response.json();
  },

  /**
   * Get session data
   */
  async getSession(sessionId: string): Promise<SessionData> {
    if (MOCK_MODE) {
      return {
        session_id: sessionId,
        title: 'モック特許解析',
        status: 'completed',
        current_step: 19,
        user_input: 'mock',
        pdf_files: [],
        extracted_keywords: [],
        extracted_ipc_codes: [],
        grouped_ipc_codes: {},
        search_formulas: [],
        selected_formula_ids: [],
        final_patents: [],
        realization_methods: [],
        selected_realization_ids: [],
        components: [],
        patent_qualities: [],
        excel_file: null,
        created_at: '',
        updated_at: '',
      } as unknown as SessionData;
    }
    const response = await fetchWithAuth(`/api/phase3/session/${sessionId}`);

    if (!response.ok) {
      const ct = (response.headers.get('content-type') || '').toLowerCase();
      if (ct.includes('application/json')) {
        const error = (await response.json()) as { detail?: string; error_message?: string };
        const detail = error.detail || error.error_message || '';
        if (detail) throw new Error(detail);
      }
      if (response.status === 410) {
        throw new Error(
          '削除されたセッションです。管理画面から新しいセッションを開始してください。'
        );
      }
      if (response.status === 404) {
        throw new Error(
          'セッションが見つかりません。管理画面から開き直すか、新しいセッションを開始してください。'
        );
      }
      throw new Error('セッション情報の取得に失敗しました');
    }

    return response.json();
  },

  /**
   * Admin-only read-only session preview.
   */
  async getAdminSessionPreview(sessionId: string): Promise<SessionData> {
    if (MOCK_MODE) {
      return this.getSession(sessionId);
    }
    const cacheBuster = Date.now().toString();
    const response = await fetchWithAuth(
      `/api/phase3/admin/session-preview/${encodeURIComponent(sessionId)}?_=${cacheBuster}`,
      {
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-store, no-cache, max-age=0, must-revalidate',
          Pragma: 'no-cache',
        },
      }
    );

    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    const isJson = contentType.includes('application/json');

    if (!response.ok) {
      const error = isJson ? await response.json().catch(() => ({})) : {};
      throw new Error(error.detail || 'セッションプレビューの取得に失敗しました。');
    }

    if (!isJson) {
      throw new Error(
        'セッションプレビューAPIからJSONではない応答が返りました。ブラウザキャッシュを無効化して再読み込みするか、APIルーティングをご確認ください。'
      );
    }

    return response.json();
  },

  /**
   * Export session data
   */
  async exportSession(sessionId: string, format: string): Promise<{ file_url: string }> {
    const response = await fetchWithAuth(
      `/api/phase3/export?session_id=${sessionId}&format=${format}`
    );

    if (!response.ok) {
      throw new Error('エクスポートに失敗しました');
    }

    return response.json();
  },
};
